import { supabase } from '../lib/supabase';

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export async function saveContent(contentData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('content_posts')?.insert({
        user_id: user?.id,
        original_idea: contentData?.originalIdea || 'Untitled',
        code_snippet: contentData?.codeSnippet || null,
        linkedin_content: contentData?.linkedin || null,
        twitter_content: contentData?.twitter || null,
        ai_model: contentData?.aiModel || 'default',
        writing_style: contentData?.writingStyle || 'default'
      })?.select()?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.error('Data error:', error?.message);
        return null;
      }
    }

    return data;
  } catch (error) {
    console.error('Error saving content:', error);
    throw error;
  }
}

export async function getUserContent() {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError) {
      console.error('Auth error in getUserContent:', authError);
      throw new Error('User not authenticated');
    }
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('content_posts')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.error('Data error:', error?.message);
        return [];
      }
    }

    // Transform data to create separate posts for each platform
    const transformedPosts = [];
    
    if (data && data.length > 0) {
      data.forEach(record => {
        // Only show posts that were actually published (have a platform post ID)
        if (record.linkedin_content && record.linkedin_content.trim() && record.linkedin_post_id) {
          transformedPosts.push({
            id: `${record.id}_linkedin`,
            platform: 'linkedin',
            content: record.linkedin_content,
            publishedAt: formatDate(record.created_at),
            likes: 0,
            comments: 0,
            shares: 0,
            originalIdea: record.original_idea,
            codeSnippet: record.code_snippet,
            aiModel: record.ai_model,
            writingStyle: record.writing_style,
            createdAt: record.created_at
          });
        }

        if (record.twitter_content && record.twitter_content.trim() && record.twitter_post_id) {
          transformedPosts.push({
            id: `${record.id}_twitter`,
            platform: 'twitter',
            content: record.twitter_content,
            publishedAt: formatDate(record.created_at),
            likes: 0,
            comments: 0,
            shares: 0,
            originalIdea: record.original_idea,
            codeSnippet: record.code_snippet,
            aiModel: record.ai_model,
            writingStyle: record.writing_style,
            createdAt: record.created_at
          });
        }
      });
    }

    // Sort by creation date (most recent first)
    transformedPosts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return transformedPosts;
  } catch (error) {
    console.error('Error fetching content:', error);
    throw error;
  }
}

// Helper function to format dates
function formatDate(dateString) {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}